import { ReactElement } from 'react';
import { Modal } from '@weareopenr/component-library';
import { FieldValues } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'src/routes';
import { useMenuSet } from 'src/hooks/useMenuSet';
import { useMenuSetPublishDestinations } from 'src/hooks/useMenuSetPublishDestinations/useMenuSetPublishDestinations';
import { usePublishMenuSet } from 'src/hooks/usePublishMenuSet/usePublishMenuSet';
import { usePrefetchSchedules } from 'src/hooks/useSchedules/useSchedules';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { oneInstancePerExternalSystem } from 'src/hooks/useTimetablePublishDestinations/oneInstancePerExternalSystem';
import { formatPublication } from 'src/hooks/useMenu/formatMenu/formatMenu';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { ScheduleStatus } from 'src/api/getSchedules/types';
import PublishSetupView from 'src/components/PublishModal/PublishSetupView';
import SummaryView from 'src/components/PublishModal/SummaryView/SummaryView';
import { IPublishContext, PublishFlow, PublishFlowGate } from 'src/components/PublishModal/PublishFlow/PublishFlow';
import { IPublishDestination } from 'src/components/PublishModal/types';
import { buildPublishSelection } from 'src/components/PublishModal/utilities/buildPublishSelection/buildPublishSelection';
import { destinationId } from 'src/components/PublishModal/utilities/destinationId/destinationId';
import ModalImg from 'src/assets/images/chef-plating.png';
import { IModalProps } from './types';

const EYEBROW = 'Menu Set Publish';

const MenuSetPublishFlow = ({
  menuSetId,
  menuSetName,
  isTrial,
  closeModal,
  publicationData,
}: {
  menuSetId: string;
  menuSetName: string;
  isTrial: boolean;
  publicationData: IPublishDestination[];
  closeModal: () => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { trackEvent } = useUserTracking();
  const publishMenuSet = usePublishMenuSet(menuSetId);
  const prefetchSchedules = usePrefetchSchedules(ScheduleStatus.SCHEDULED);
  // The schedule card only polls once it reads VALIDATING, so refetch schedules when validation is
  // accepted in case Upcoming Publishes fetched them before the report existed
  const validate = useValidate({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [getBrandId(), QueryKeys.schedules] }),
  });

  const showSchedule = !isTrial;
  const destinationNames = new Map(
    publicationData.map((destination) => [destinationId(destination), destination.name] as const),
  );

  const handlePublish = async (
    values: FieldValues,
    { publicationDateTime, isScheduled, notifySuccess }: IPublishContext,
  ) => {
    const selectedVenueIds: Record<string, boolean> = Object.fromEntries(
      ((values['venues'] ?? []) as string[]).map((venueId) => [venueId, true]),
    );
    const { channels: instances, channelIds } = buildPublishSelection(
      publicationData,
      values['selectedChannels'],
      selectedVenueIds,
    );

    const response = await publishMenuSet.mutateAsync({
      price_scheme_id: values['price-scheme'],
      instances: instances.map(({ instance_id, venues }) => ({ instance_id: instance_id as string, venues })),
      ...(publicationDateTime && {
        publication_date_time: new Date(publicationDateTime).toISOString(),
        schedule_name: values['scheduleName'],
      }),
    });

    // Validation only applies to scheduled menu set publishes - publishing
    // immediately has no schedule to attach a report to. This fires
    // fire-and-forget: the report is viewed later from the schedule card,
    // not blocking the publish success flow here.
    if (isScheduled && response?.schedule_id) {
      validate.mutate({
        criteria: {
          schedule_id: response.schedule_id,
          price_scheme_id: values['price-scheme'],
          menu_set_id: menuSetId,
          rules: getValidationRules(EVENT_TYPES.PUBLISH_MENU_SET),
        },
      });
    }

    trackEvent('menu_set_published', {
      menuSetId,
      menuSetName,
      channelCount: instances.length,
      isScheduled,
      isTrial,
    });

    // The snackbar is app-wide, so it survives the navigation below.
    notifySuccess(channelIds.flatMap((id) => destinationNames.get(id) ?? []));
    // The handler writes the event logs before responding, so the refetched menu set already carries
    // the new publication.event_id that the status redirect reads.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKeys.menuSet, getBrandId(), menuSetId] }),
      queryClient.invalidateQueries({ queryKey: [QueryKeys.menuSets] }),
    ]);
    if (isScheduled) {
      await prefetchSchedules();
      navigate(Routes.PUBLISH_SCHEDULE, { state: { newScheduleId: response?.schedule_id } });
    } else {
      navigate(`${Routes.MENU_SETS}/${menuSetId}/status`);
    }
  };

  return (
    <PublishFlow
      entityName={menuSetName}
      publicationData={publicationData}
      // Menu publish preselects the menu's linked venues; a Menu Set has none, so the user picks every venue.
      defaultVenueIds={[]}
      requirePriceScheme
      showSchedule={showSchedule}
      eyebrow={EYEBROW}
      closeModal={closeModal}
      renderSetup={(form) => (
        <PublishSetupView
          {...form}
          menuName={menuSetName}
          publicationData={publicationData}
          showSchedule={showSchedule}
          eyebrow={EYEBROW}
          getDisabledReason={(destination, selectedIds) =>
            oneInstancePerExternalSystem(publicationData, destination, selectedIds)
          }
        />
      )}
      renderSummary={({ watch }) => (
        <SummaryView
          watch={watch}
          menuName={menuSetName}
          publicationData={publicationData}
          snapshotLabel="Menu set snapshot"
        />
      )}
      onPublish={handlePublish}
      errorTags={{ action: 'publish_menu_set', menuSetId }}
    />
  );
};

const MenuSetPublishFlowWrapper = ({
  menuSetId,
  isTrial,
  closeModal,
}: {
  menuSetId: string;
  isTrial: boolean;
  closeModal: () => void;
}) => {
  const { data: menuSetData, isLoading: isMenuSetLoading } = useMenuSet(menuSetId);
  const menuSet = menuSetData?.data[0];
  const publication = menuSet?.publication ? formatPublication(menuSet.publication) : undefined;
  const {
    data: publicationData,
    isLoading: isPublicationDataLoading,
    isError,
  } = useMenuSetPublishDestinations(isTrial, publication);

  return (
    <PublishFlowGate
      isLoading={isMenuSetLoading || isPublicationDataLoading}
      isUnavailable={publicationData.length === 0 || isError || !menuSet}
    >
      <MenuSetPublishFlow
        menuSetId={menuSetId}
        menuSetName={menuSet?.name ?? ''}
        isTrial={isTrial}
        closeModal={closeModal}
        publicationData={publicationData}
      />
    </PublishFlowGate>
  );
};

const MenuSetPublishModal = ({ closeModal, isOpen, menuSetId, isTrial = false }: IModalProps): ReactElement => {
  return (
    <Modal closeModal={closeModal} open={isOpen} image={ModalImg} imageAlt="Picture of a chef plating up">
      <MenuSetPublishFlowWrapper menuSetId={menuSetId} isTrial={isTrial} closeModal={closeModal} />
    </Modal>
  );
};

export default MenuSetPublishModal;
